/* Combo integrity guard (Jamie, 2026-09-06).

   🔴 THE LEAK THIS CLOSES. The combo saving is applied by the `combo-saving` Shopify Function,
   which groups cart lines by `_bundle_id`, reads the `Combo` string off any surviving line in the
   group, decides the saving from that string, and caps it at the group's remaining subtotal:

       const amount = Math.min(saving, groupTotal);

   The cap stops the discount exceeding the group, but NOTHING checks the group is still COMPLETE.
   Remove the head unit from a $400 combo and the $400 lands on whatever is left, so a pair of
   door pods can walk out at a few dollars. Observed live by Jamie.

   This file removes the whole group when any one of its lines is removed, which is the same rule
   `pvs-cart-select.js` already applies to the per-line checkbox. A combo is configured on the
   product page; it is not something to dismantle a line at a time in the cart.

   ⚠️ THIS IS A FRONT-END GUARD AND ONLY STOPS THE ACCIDENT. A crafted POST to /cart/change.js
   still leaves a part-emptied group, and the Function will still discount it. The real fix is a
   completeness test inside the Function. ⛔ Do not treat this file as the backstop. */
(function () {
  const routes = () => (window.theme && window.theme.routes) || {};

  async function cartJson() {
    const response = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  function refreshCart() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      cartItems.refresh();
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      return;
    }
    window.location.assign(routes().cart);
  }

  function showError(message) {
    const errors = document.getElementById('cart-errors');
    if (!errors) return;
    errors.textContent = message;
    errors.hidden = false;
  }

  async function removeGroup(bundleId) {
    const cart = await cartJson();
    const group = cart.items.filter(
      (item) => item.properties && item.properties._bundle_id === bundleId
    );
    if (!group.length) return;

    const updates = {};
    group.forEach((item) => { updates[item.key] = 0; });

    const response = await fetch(`${routes().cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates })
    });
    if (!response.ok) throw new Error(`cart/update ${response.status}`);
    refreshCart();
  }

  /* Capture phase on purpose: cart-items.js binds its own click handler on the <cart-items>
     element and calls updateQuantity(index, 0) for a single line. Running first and stopping
     propagation is what keeps it from removing just the one line. */
  document.addEventListener('click', (evt) => {
    const el = evt.target;
    if (!el || typeof el.matches !== 'function' || !el.matches('.js-remove-item')) return;

    const row = el.closest('.cart-item');
    const bundleId = row && row.dataset.bundleId;
    if (!bundleId) return;               // an ordinary line: let the theme handle it

    evt.preventDefault();
    evt.stopPropagation();

    const label = row.querySelector('.pvsc-line__combo');
    const name = label ? label.textContent.trim() : 'this combo';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Removing this line removes the whole combo, because the rest is priced as a set. Remove ${name}?`)) return;

    removeGroup(bundleId).catch(() => {
      showError('That combo could not be removed. Refresh the page and try again.');
    });
  }, true);

  /* A component's quantity is not independently editable for the same reason: the saving is a
     fixed amount for a configured set, so thinning the set inflates the effective discount.
     The stepper is locked on bundle lines and the whole group is removed instead. */
  function lockBundleSteppers() {
    document.querySelectorAll('.cart-item[data-bundle-id]').forEach((row) => {
      row.querySelectorAll('.qty-input__btn, .qty-input__input').forEach((el) => {
        el.setAttribute('disabled', '');
        el.setAttribute('aria-disabled', 'true');
      });
      const qty = row.querySelector('.pvsc-line__qty');
      if (qty && !qty.querySelector('.pvsc-line__qtylock')) {
        const note = document.createElement('span');
        note.className = 'pvsc-line__qtylock';
        note.textContent = 'Set by the combo';
        qty.appendChild(note);
      }
    });
  }

  document.addEventListener('DOMContentLoaded', lockBundleSteppers);
  ['on:line-item:change', 'dispatch:cart-drawer:refresh', 'cart:updated'].forEach((ev) => {
    document.addEventListener(ev, () => setTimeout(lockBundleSteppers, 80));
  });
  lockBundleSteppers();
}());
